/* cz-cart-recommendations.js
   Custom element <cz-cart-recommendations>:
   - Fetch /recommendations/products.json al connectedCallback
   - Render card con brand, titolo, prezzo, immagine, pulsante "+"
   - Esclude prodotti già in cart
   - Re-fetch su cz:cart:updated
   - Click "+" su prodotto single-variant → quick add
   - Click "+" su prodotto multi-variant → apre <cz-cart-reco-variants> (Task 15)
   - "chiudi i suggerimenti" → nasconde la sezione fino al prossimo cz:cart:updated
*/

class CzCartRecommendations extends HTMLElement {
  connectedCallback() {
    this.addon = this.closest('cz-cart-addon');
    this.track = this.querySelector('[data-track]');
    this.nav = this.querySelector('[data-nav]');
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    this.closeBtn = this.querySelector('[data-close]');

    this.productId = this.dataset.productId;
    this.limit = parseInt(this.dataset.limit || '6', 10);
    // Prodotti-servizio da non consigliare: gli handle arrivano dalle stesse
    // impostazioni che il tema usa per applicare incisione e pacchetto, non da
    // una lista nuova da tenere allineata a mano.
    this.esclusi = (this.dataset.esclusi || '')
      .toLowerCase().split(',').map((s) => s.trim()).filter(Boolean);

    if (!this.productId) return;

    this.prevBtn?.addEventListener('click', () => this.scrollBy(-1));
    this.nextBtn?.addEventListener('click', () => this.scrollBy(+1));
    this.closeBtn?.addEventListener('click', () => this.closeAddon());

    document.addEventListener('cz:cart:updated', () => this.fetchAndRender());

    this.fetchAndRender();
  }

  // Stesso criterio del filtro Liquid in related-products e main-product.
  eServizio(p) {
    if (!this.esclusi.length) return false;
    return this.esclusi.includes(String(p.handle || '').toLowerCase().trim());
  }

  async fetchAndRender() {
    try {
      const url = `/recommendations/products.json?product_id=${this.productId}&limit=${this.limit + 5}&intent=related`;
      const res = await fetch(url);
      const data = await res.json();
      const products = (data.products || [])
        .filter(p => !this.isInCart(p.id))
        .filter(p => !this.eServizio(p))
        .slice(0, this.limit);

      if (products.length === 0) {
        if (this.addon) this.addon.hidden = true;
        return;
      }

      this.renderCards(products);
      if (this.addon) this.addon.hidden = false;
      this.updateNav();
    } catch (err) {
      console.warn('cz-cart-recommendations fetch failed', err);
      if (this.addon) this.addon.hidden = true;
    }
  }

  closeAddon() {
    if (this.addon && typeof this.addon.toggle === 'function' && this.addon.dataset.open === 'true') {
      this.addon.toggle();
    }
  }

  isInCart(productId) {
    const cartProductIds = Array.from(document.querySelectorAll('.cz-cart-item'))
      .map(el => parseInt(el.dataset.productId, 10))
      .filter(Boolean);
    return cartProductIds.includes(productId);
  }

  renderCards(products) {
    this.track.innerHTML = products.map(p => this.cardHTML(p)).join('');
    this.track.querySelectorAll('[data-add]').forEach(btn => {
      btn.addEventListener('click', () => this.handleAdd(btn, products.find(p => p.id == btn.dataset.productId)));
    });
  }

  cardHTML(product) {
    const firstVariant = product.variants[0];
    const isAvailable = product.variants.some(v => v.available);
    const img = product.featured_image
      ? `<img class="cz-cart-reco__img" src="${product.featured_image}" alt="${this.escapeHTML(product.title)}" loading="lazy" width="180" height="180">`
      : '';
    const price = this.formatMoney(firstVariant?.price);

    return `
      <article class="cz-cart-reco__card" data-product-id="${product.id}">
        <div class="cz-cart-reco__media">
          <a href="${product.url}" aria-label="${this.escapeHTML(product.title)}">${img}</a>
          ${isAvailable
            ? `<button type="button" class="cz-cart-reco__plus" data-add data-product-id="${product.id}" aria-label="Aggiungi al carrello">+</button>`
            : `<div class="cz-cart-reco__sold-out">Esaurito</div>`}
        </div>
        <div class="cz-cart-reco__brand">${this.escapeHTML(product.vendor || '')}</div>
        <a href="${product.url}" class="cz-cart-reco__name">${this.escapeHTML(product.title)}</a>
        <div class="cz-cart-reco__price">${price}</div>
      </article>
    `;
  }

  async handleAdd(btn, product) {
    const isSingleVariant = product.variants.length === 1;
    if (isSingleVariant) {
      btn.disabled = true;
      btn.textContent = '…';
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: product.variants[0].id, quantity: 1 }),
        });
        btn.textContent = '✓';
        setTimeout(() => {
          btn.textContent = '+';
          btn.disabled = false;
        }, 1200);
        document.dispatchEvent(new CustomEvent('cz:cart:updated', { detail: { source: 'reco-quick-add' } }));
      } catch (err) {
        btn.textContent = '+';
        btn.disabled = false;
      }
      return;
    }

    // Multi-variant: apre drawer varianti interno (Task 15)
    this.openVariantPicker(product);
  }

  openVariantPicker(product) {
    let picker = document.querySelector('cz-cart-reco-variants');
    if (!picker) {
      picker = document.createElement('cz-cart-reco-variants');
      document.body.appendChild(picker);
    }
    picker.open(product);
  }

  scrollBy(direction) {
    const cardWidth = this.track.querySelector('.cz-cart-reco__card')?.offsetWidth || 100;
    this.track.scrollBy({ left: direction * (cardWidth + 10), behavior: 'smooth' });
    setTimeout(() => this.updateNav(), 350);
  }

  updateNav() {
    if (!this.prevBtn) return;
    const max = this.track.scrollWidth - this.track.clientWidth;
    this.prevBtn.disabled = this.track.scrollLeft <= 1;
    this.nextBtn.disabled = this.track.scrollLeft >= max - 1;
    if (max <= 0) this.nav.hidden = true;
    else this.nav.hidden = false;
  }

  formatMoney(cents) {
    if (cents == null) return '';
    const euros = Math.floor(cents / 100);
    const remainder = cents % 100;
    if (remainder === 0) return `€ ${euros}`;
    const c = remainder < 10 ? `0${remainder}` : remainder;
    return `€ ${euros},${c}`;
  }

  escapeHTML(str) {
    return String(str || '').replace(/[&<>"']/g, m => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[m]));
  }
}

if (!customElements.get('cz-cart-recommendations')) {
  customElements.define('cz-cart-recommendations', CzCartRecommendations);
}

class CzCartRecoVariants extends HTMLElement {
  /* La costruzione del DOM vive in connectedCallback, NON nel constructor:
     le specifiche dei custom element vietano figli nel constructor e
     document.createElement('cz-cart-reco-variants') esplodeva con
     NotSupportedError ("the result must not have children") — il "+"
     sui prodotti multi-taglia sembrava morto. */
  connectedCallback() {
    if (this._costruito) return;
    this._costruito = true;
    this.innerHTML = `
      <div class="cz-cart-reco-variants" data-drawer hidden>
        <div class="cz-cart-reco-variants__overlay" data-close></div>
        <div class="cz-cart-reco-variants__content" role="dialog" aria-modal="true">
          <div class="cz-cart-reco-variants__handle"></div>
          <button type="button" class="cz-cart-reco-variants__close" data-close aria-label="Chiudi">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 18 17"><path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"/></svg>
          </button>
          <p class="cz-cart-reco-variants__title" data-title>Seleziona la taglia</p>
          <div class="cz-cart-reco-variants__options" data-options></div>
        </div>
      </div>
    `;
    this.drawer = this.querySelector('[data-drawer]');
    this.titleEl = this.querySelector('[data-title]');
    this.optionsEl = this.querySelector('[data-options]');

    this.querySelectorAll('[data-close]').forEach(el => el.addEventListener('click', () => this.close()));
  }

  open(product) {
    this.product = product;
    // L'API /recommendations restituisce options come oggetti {name, values},
    // il quick-add di /products/*.js come stringhe: si accettano entrambi.
    const opt0 = product.options?.[0];
    let mainOptionName = (typeof opt0 === 'string' ? opt0 : opt0?.name) || 'Variante';
    // Il dato dice "Size" ma il sito parla italiano (stessa convenzione del
    // variant picker della scheda prodotto).
    if (/size|misura|taglia/i.test(mainOptionName)) mainOptionName = 'taglia';
    this.titleEl.textContent = `Seleziona ${mainOptionName.toLowerCase()}`;

    this.optionsEl.innerHTML = product.variants.map((v, i) => {
      const label = v.title || v.option1 || `Variante ${i+1}`;
      const disabled = !v.available ? 'disabled' : '';
      return `
        <label class="cz-cart-reco-variants__option ${disabled ? 'is-disabled' : ''}">
          <input type="radio" name="cz-reco-variant" value="${v.id}" ${disabled}>
          <span>${this.escapeHTML(label)}</span>
        </label>
      `;
    }).join('');

    this.optionsEl.querySelectorAll('input[type="radio"]').forEach(input => {
      input.addEventListener('change', () => this.addVariant(parseInt(input.value, 10)));
    });

    this.drawer.hidden = false;
    document.documentElement.classList.add('cz-reco-picker-open');
    requestAnimationFrame(() => this.drawer.classList.add('is-open'));
  }

  async addVariant(variantId) {
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });
      document.dispatchEvent(new CustomEvent('cz:cart:updated', { detail: { source: 'reco-variant-add' } }));
      this.close();
    } catch (err) {
      console.warn('cz-cart-reco-variants add failed', err);
    }
  }

  close() {
    document.documentElement.classList.remove('cz-reco-picker-open');
    this.drawer.classList.remove('is-open');
    setTimeout(() => { this.drawer.hidden = true; }, 200);
  }

  escapeHTML(str) {
    return String(str || '').replace(/[&<>"']/g, m => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[m]));
  }
}

if (!customElements.get('cz-cart-reco-variants')) {
  customElements.define('cz-cart-reco-variants', CzCartRecoVariants);
}
